import { makeAutoObservable } from "mobx";
import { IChangeable } from "../../../stores/types";
import { AMOUNT_TYPE, FEE_TYPES_REQUIRING_AMOUNT_INPUT, FEE_TYPE, FEE_AMOUNT_OPTIONS, FEE_TYPE_FREE, FEE_TYPE_INCLUDS_COST } from "../../policies/policy-types";
import { IOption } from "../../../components/inputs/select-dropdown/select-dropdown";
import { ICRMAFeeDto } from "../../../models/User";
import dashboardSettingsStore from "../../../stores/dashboard-settings-store";
import { PROFILE_CAPABILITIES } from "../../../models/ProfileCapabilities";

interface ICRmaFeeEditorStoreChanges extends Partial<ICRMAFeeDto> {}

class CRmaFeeEditorStore implements IChangeable<ICRMAFeeDto> {

    private _changes: ICRmaFeeEditorStoreChanges = {};
    private _model: ICRMAFeeDto;

    constructor(model: ICRMAFeeDto) {
        makeAutoObservable(this, {}, {autoBind: true});
        this._model = model;
	}

    public get isCharged(): boolean { return this._changes.is_charged ?? this._model.is_charged; }
    public get chargeTax(): boolean { return this._changes.charge_tax ?? this._model.charge_tax; }
    public get feeType(): FEE_TYPE { return this._changes.fee_type ?? this._model.fee_type; }
    public get amountType(): AMOUNT_TYPE { return this._changes.amount_type ?? this._model.amount_type; }
    public get amount(): number { return this._changes.amount ?? this._model.amount; }

    private _clearUnneededChanges() {
        Object.keys(this._changes).forEach(key => {
            const theKey = key as keyof ICRMAFeeDto;
            if (this._changes[theKey] === this._model?.[theKey]) {
                delete this._changes[theKey];
            }
        });
    }

    public setReturnFeeAmountType(v: AMOUNT_TYPE): void {
        if (v === FEE_TYPE_FREE as AMOUNT_TYPE) {
            this.setIsCharged(false);
            return;
        }
        this.setIsCharged(true);
        this._changes.amount_type = v;
        this._clearUnneededChanges();
    }

    public setAmountType(v: AMOUNT_TYPE): void {
        this._changes.amount_type = v;
        this._clearUnneededChanges();
    }

    public setAmount(v: number): void {
        this._changes.amount = v;
        this._clearUnneededChanges();
    }

    public setChargeTax(v: boolean): void {
        this._changes.charge_tax = v;
        this._clearUnneededChanges();
    }

    public setIsCharged(v: boolean): void {
        this._changes.is_charged = v;
        this._clearUnneededChanges();
    }

    public setFeeType(v: FEE_TYPE): void {
        if (v === FEE_TYPE_FREE as FEE_TYPE) {
            this.setIsCharged(false);
            return;
        }
        this._changes.fee_type = v;
        if (v === FEE_TYPE.COST) {
            /** if cost fee is selected, set amount to 0
             * to avoid confusion and prevent any calculations based on the amount
            */
            this.setAmount(0)
            this.setAmountType(AMOUNT_TYPE.FIXED);
        }
        this.setIsCharged(true);
        if (this.isTaxForcedNoCharge) {
            this.setChargeTax(false);
        }
        this._clearUnneededChanges();
    }

    /**
     * Whether tax must be forced to "don't charge" (and the control locked) for this fee.
     * True for Global-E international merchants when the return shipping label fee is cost-based
     * (Cost / Cost + per item / Cost + per shipment).
     */
    public get isTaxForcedNoCharge(): boolean {
        if (!FEE_TYPE_INCLUDS_COST.includes(this.feeType)) {
            return false;
        }
        const capability = dashboardSettingsStore.resolveCapability(PROFILE_CAPABILITIES.FEE_TAXES_CALCULATION);
        return !!capability.forceNoTaxForCostBasedShippingFee;
    }

    public getUpdatedModel(): ICRMAFeeDto {
        return { ...this._model, ...this._changes };
    }

    public discardChanges(): void {
        this._changes = {};
    }

    public hasChanges() {
        return !!Object.keys(this._changes).length;
    }

    public allowsAmountEdit(): boolean {
		return !([FEE_TYPE.COST_PLUS_SHIPMENT_FEE, FEE_TYPE.SHIPMENT_FEE].includes(this.feeType));
	}

	public showsAmountInputs(): boolean {
		return FEE_TYPES_REQUIRING_AMOUNT_INPUT.includes(this.feeType);
	}

	public usesPercentageAmount(): boolean {
		if (!this.allowsAmountEdit()) {
			return false;
		}
		return (
			this.amountType === AMOUNT_TYPE.ITEM_PERCENTAGE_PAID_PRICE ||
			this.amountType === AMOUNT_TYPE.ITEM_PERCENTAGE_CATALOG_PRICE
		);
	}

    public returnFeeAmountTypeOptions(): IOption[] {
        const options: IOption[] = [...FEE_AMOUNT_OPTIONS].filter((o) =>
            [AMOUNT_TYPE.FIXED, AMOUNT_TYPE.ITEM_PERCENTAGE_PAID_PRICE].includes(o.value as AMOUNT_TYPE),
        );
        return [{ label: 'Free', value: FEE_TYPE_FREE }, ...options];
    }

    public labelFeeAmountTypeOptions(): IOption[] {
        return [...FEE_AMOUNT_OPTIONS]
    }

    public allShippingLabelFeeOptions(): IOption[] {
        const options: IOption[] = [
            { label: 'Free', value: FEE_TYPE_FREE },
            { label: "Cost", value: FEE_TYPE.COST },
            { label: "Cost + per item fee", value: FEE_TYPE.COST_PLUS_ITEM_FEE },
            { label: "Cost + per shipment fee", value: FEE_TYPE.COST_PLUS_SHIPMENT_FEE },
            { label: "Charge fixed amount", value: FEE_TYPE.SHIPMENT_FEE },
        ];
        return [...options];
    }

}

export default CRmaFeeEditorStore;